import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import useToggle from '../../../../hooks/useToggle';
import { hasFeature } from '../../../../shared-components/contexts/InstitutionContext';
import DialogTransition from '../../../../shared-components/DialogTransition';
import AlertIcon from '../../../../shared-components/Icons/AlertIcon';
import EncouragementIcon from '../../../../shared-components/Icons/EncouragementIcon';
import UpdateIcon from '../../../../shared-components/Icons/UpdateIcon';
import { IMultiStudentCreateAlertRequest } from '../../../../shared-interfaces/Notation/Alert/IMultiStudentCreateAlertRequest';
import { IMultiStudentCreateEncouragementRequest } from '../../../../shared-interfaces/Notation/Encouragement/IMultiStudentCreateEncouragementRequest';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import { IMultiStudentCreateUpdateNoteRequest } from '../../../../shared-interfaces/Notation/UpdateNote/IMultiStudentCreateUpdateNoteRequest';
import AlertService from '../../../../shared-services/Notation/AlertService';
import EncouragementService from '../../../../shared-services/Notation/EncouragementService';
import UpdateNoteService from '../../../../shared-services/Notation/UpdateNoteService';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import CreateNotationForm from './CreateNotationForm';
import NotationSelectionCard from './NotationSelectionCard';

interface ICreateNotationDialog {
  open: boolean;
  close: () => void;
  onComplete: (n: INotation[]) => void;
}

enum Steps {
  selectNotation,
  createAlert,
  createUpdate,
  createEncouragement,
}

const CreateNotationDialog = (props: ICreateNotationDialog) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const { enqueueSnackbar } = useSnackbar();

  const [isSaving, toggleIsSaving] = useToggle(false);
  const [currentStep, setCurrentStep] = useState(Steps.selectNotation);

  useEffect(() => {
    setTimeout(() => {
      setCurrentStep(Steps.selectNotation);
    }, 500);
  }, [props.close]);

  function createAlert(request: IMultiStudentCreateAlertRequest) {
    toggleIsSaving();

    if (request.studentIds.length > 5) {
      AlertService.bulkCreateAlert(request)
        .then(n => {
          props.onComplete([]);
          enqueueSnackbar(
            'Alerts Created. NOTE: It may take up to 5 minutes to reflect in the feed.',
            { variant: 'success' },
          );
        })
        .catch(e => {
          console.log(e);
          enqueueSnackbar('Failed to Create Alert', { variant: 'error' });
        })
        .finally(() => {
          toggleIsSaving();
          props.close();
        });
    } else {
      AlertService.createMultiple(request)
        .then(n => {
          props.onComplete(n);
          enqueueSnackbar('Alert Created', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Failed to Create Alert', { variant: 'error' });
        })
        .finally(() => {
          toggleIsSaving();
          props.close();
        });
    }
  }

  function createUpdate(request: IMultiStudentCreateUpdateNoteRequest) {
    toggleIsSaving();
    UpdateNoteService.createMultiple(request)
      .then(n => {
        props.onComplete(n);
        enqueueSnackbar('Update Created', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to Create Update', { variant: 'error' });
      })
      .finally(() => {
        toggleIsSaving();
        props.close();
      });
  }

  function createEncouragement(
    request: IMultiStudentCreateEncouragementRequest,
  ) {
    toggleIsSaving();
    EncouragementService.createMultiple(request)
      .then(n => {
        props.onComplete(n);
        enqueueSnackbar('Encouragement Created', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to Create Encouragement', { variant: 'error' });
      })
      .finally(() => {
        toggleIsSaving();
        props.close();
      });
  }

  const NotationSelection = () => {
    return (
      <DialogContent>
        <Grid container direction="column" style={{ marginBottom: '15px' }}>
          <Grid item>
            <NotationSelectionCard
              title="Update"
              icon={<UpdateIcon />}
              onClick={() => setCurrentStep(Steps.createUpdate)}
            >
              Updates allow you to share information about a student with
              members of their Success Network.
            </NotationSelectionCard>
          </Grid>
          {hasFeature('Alerts') && (
            <Grid item>
              <NotationSelectionCard
                title="Alert"
                icon={<AlertIcon />}
                onClick={() => setCurrentStep(Steps.createAlert)}
              >
                Alerts are very important updates and will require a response
                from the Success Network.
              </NotationSelectionCard>
            </Grid>
          )}
          {hasFeature('Encouragements') && (
            <Grid item>
              <NotationSelectionCard
                title="Encouragement"
                icon={<EncouragementIcon />}
                onClick={() => setCurrentStep(Steps.createEncouragement)}
              >
                Encouragements help students adopt a growth mindset and reorient
                their attitudes toward learning.
              </NotationSelectionCard>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    );
  };

  return (
    <Dialog
      open={props.open}
      fullScreen={isXs}
      //@ts-ignore
      TransitionComponent={isXs ? DialogTransition : undefined}
    >
      <DialogTitle>
        <Box display="flex">
          <Box flexGrow={1}>
            {currentStep !== Steps.selectNotation && (
              <IconButton
                aria-label="Go To Previous Step"
                onClick={() => setCurrentStep(Steps.selectNotation)}
                size="large"
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}
            {currentStep === Steps.selectNotation
              ? 'Choose a Notation Type'
              : currentStep === Steps.createUpdate
              ? 'Create Update'
              : currentStep === Steps.createAlert
              ? 'Create Alert'
              : currentStep === Steps.createEncouragement
              ? 'Create Encouragement'
              : 'Finish'}
          </Box>
          <Box alignItems="flex-start">
            <IconButton
              aria-label="Close"
              style={{ padding: 0 }}
              onClick={props.close}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {currentStep === Steps.selectNotation ? (
        <Fade in={currentStep === Steps.selectNotation}>
          <div>
            <NotationSelection />
          </div>
        </Fade>
      ) : currentStep === Steps.createUpdate ? (
        <Fade in={currentStep === Steps.createUpdate}>
          <div>
            <CreateNotationForm
              notationType={NotationType.Update}
              onSave={r =>
                createUpdate(r as IMultiStudentCreateUpdateNoteRequest)
              }
              onCancel={() => props.close()}
            />
          </div>
        </Fade>
      ) : currentStep === Steps.createAlert ? (
        <Fade in={currentStep === Steps.createAlert}>
          <div>
            <CreateNotationForm
              notationType={NotationType.Alert}
              onSave={r => createAlert(r as IMultiStudentCreateAlertRequest)}
              onCancel={() => props.close()}
            />
          </div>
        </Fade>
      ) : currentStep === Steps.createEncouragement ? (
        <Fade in={currentStep === Steps.createEncouragement}>
          <div>
            <CreateNotationForm
              notationType={NotationType.Encouragement}
              onSave={r =>
                createEncouragement(
                  r as IMultiStudentCreateEncouragementRequest,
                )
              }
              onCancel={() => props.close()}
            />
          </div>
        </Fade>
      ) : (
        <div>You shouldn't be here</div>
      )}
      {isSaving && <LinearProgress />}
    </Dialog>
  );
};

export default CreateNotationDialog;
